import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
    XCircle,
    FileText,
    Archive,
    Activity,
    CheckCircle,
    SlidersHorizontal,
    Key,
    Trash2
} from 'lucide-react';

const BeneficiaryAdminModal = ({ client, onClose, onAction, hasPermission }) => {
    if (!client) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl relative"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-inner">
                            {client.firstName?.[0]}{client.lastName?.[0]}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white">{client.firstName} {client.lastName}</h3>
                            <p className="text-sm text-slate-400">Panneau d'administration</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
                        <XCircle size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[70vh]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Group: Administratif */}
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <FileText size={14} /> Administratif
                            </h4>
                            <div className="bg-slate-800/50 rounded-xl p-2 border border-slate-800 space-y-1">
                                <button onClick={() => { onAction('generate-document', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <FileText size={18} className="text-indigo-400" /> Générer document
                                </button>
                                <button onClick={() => { onAction('documents', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <Archive size={18} className="text-slate-400" /> Gérer documents
                                </button>
                                <button onClick={() => { onAction('contracts', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <FileText size={18} className="text-green-400" /> Contrats
                                </button>
                                <button onClick={() => { onAction('mandate', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <FileText size={18} className="text-blue-400" /> Mandat
                                </button>
                            </div>
                        </div>

                        {/* Group: Finance */}
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Activity size={14} /> Finance
                            </h4>
                            <div className="bg-slate-800/50 rounded-xl p-2 border border-slate-800 space-y-1">
                                <button onClick={() => { onAction('devis', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <FileText size={18} className="text-amber-400" /> Devis
                                </button>
                                <button onClick={() => { onAction('invoice', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <FileText size={18} className="text-emerald-400" /> Factures
                                </button>
                            </div>
                        </div>

                        {/* Group: Suivi */}
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Activity size={14} /> Suivi
                            </h4>
                            <div className="bg-slate-800/50 rounded-xl p-2 border border-slate-800 space-y-1">
                                <button onClick={() => { onAction('report', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <Activity size={18} className="text-orange-400" /> Rapports
                                </button>
                                <button onClick={() => { onAction('statistics', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <Activity size={18} className="text-teal-400" /> Statistiques
                                </button>
                                <button onClick={() => { onAction('assessment', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <CheckCircle size={18} className="text-purple-400" /> Évaluation
                                </button>
                            </div>
                        </div>

                        {/* Group: Gestion Avancée */}
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <SlidersHorizontal size={14} /> Gestion Avancée
                            </h4>
                            <div className="bg-slate-800/50 rounded-xl p-2 border border-slate-800 space-y-1">
                                <button onClick={() => { onAction('auth', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-emerald-400 flex items-center gap-3 transition-colors">
                                    <Key size={18} className="text-cyan-400" /> Gérer accès
                                </button>
                                <button onClick={() => { onAction('archive', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-3 transition-colors">
                                    <Archive size={18} className="text-slate-400" /> Archiver
                                </button>
                                {hasPermission && hasPermission({ resource: 'beneficiaries', action: 'delete' }) && (
                                    <button onClick={() => { onAction('delete', client); onClose(); }} className="w-full text-left px-3 py-2 rounded-lg text-rose-400 hover:bg-rose-500/10 flex items-center gap-3 transition-colors">
                                        <Trash2 size={18} /> Supprimer
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </motion.div>
        </div>,
        document.body
    );
};

export default BeneficiaryAdminModal;
